import { Link, Outlet, useParams } from '@tanstack/react-router';
import { type FileRoutesByTo } from '@uturn/portal/routeTree.gen';
import { buttonVariants, cn, Separator } from '@uturn/ui-kit';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

export type SideBarNavItem = {
	href: keyof FileRoutesByTo;
	title: string;
	params?: Record<string, unknown>;
};

interface SidebarNavProps extends React.HTMLAttributes<HTMLElement> {
	items: SideBarNavItem[];
}

export function SidebarNav({ className, items, ...props }: SidebarNavProps) {
	return (
		<nav
			className={cn(
				'flex space-x-2 lg:flex-col lg:space-x-0 lg:space-y-1',
				className,
			)}
			{...props}
		>
			{items.map((item) => (
				<Link
					key={item.href}
					to={item.href}
					params={item.params}
					activeOptions={{ exact: true }}
				>
					{({ isActive }) => {
						return (
							<span
								className={cn(
									buttonVariants({ variant: 'ghost' }),
									isActive
										? 'bg-accent hover:bg-accent text-accent-foreground'
										: 'hover:bg-transparent hover:underline',
									'justify-start',
								)}
							>
								{item.title}
							</span>
						);
					}}
				</Link>
			))}
		</nav>
	);
}

export function OrganizationLayout() {
	const { t } = useTranslation();
	const { orgId } = useParams({
		from: '/_protected/org/$orgId/settings',
	});

	const sidebarNavItems = (orgId: number): SideBarNavItem[] => [
		{
			title: t('pages.organisation.navigation.profile', 'Profile'),
			href: '/org/$orgId/settings',
			params: { orgId },
		},
		{
			title: t('pages.organisation.navigation.billing', 'Billing'),
			href: '/org/$orgId/settings/billing',
			params: { orgId },
		},
		{
			title: t('pages.organisation.navigation.documents', 'Documents'),
			href: '/org/$orgId/settings/documents',
			params: { orgId },
		},
		{
			title: t('pages.organisation.navigation.users', 'Users'),
			href: '/org/$orgId/settings/users',
			params: { orgId },
		},
		{
			title: t('pages.organisation.navigation.subscriptions', 'Subscriptions'),
			href: '/org/$orgId/settings/subscriptions',
			params: { orgId },
		},
		{
			title: t(
				'pages.organisation.navigation.shipment_settings',
				'Shipment settings',
			),
			href: '/org/$orgId/settings/shipment-settings',
			params: { orgId },
		},
		{
			title: t(
				'pages.organisation.navigation.blacklisted_carriers',
				'Blacklisted carriers',
			),
			href: '/org/$orgId/settings/blacklisted-carriers',
			params: { orgId },
		},
	];

	return (
		<>
			<Helmet
				title={t(
					'pages.account.organisation.title',
					'Organisation settings',
				).toString()}
			/>
			<div className="py-8 pt-6 @container/content contain-layout md:block">
				<div className="space-y-0.5">
					<h2 className="text-3xl font-bold tracking-tight">
						{t('pages.account.organisation.title', 'Organisation settings')}
					</h2>
					<p className="text-muted-foreground">
						{t(
							'pages.account.organisation.description',
							'Manage the profile and settings for your organisation',
						)}
					</p>
				</div>
				<Separator className="my-6" />
				<div className="flex flex-col space-y-8 lg:flex-row lg:space-x-12 lg:space-y-0">
					<aside className="-mx-4 lg:w-1/5">
						<SidebarNav items={sidebarNavItems(orgId)} />
					</aside>
					<div className="flex-1 lg:max-w-2xl">
						<Outlet />
					</div>
				</div>
			</div>
		</>
	);
}
